// src/pages/PostSearch.jsx
import React, { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Appfooter from "../components/Appfooter";
import Postview from "../components/Postview";
import { useAPI } from "../context/ApiContext";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(() => ({
  line: {
    display: "block",
    position: "relative",
    "&::after": {
      background: "linear-gradient(to right,#79ac78 0%,#79ac78 50%,#79ac78 100%)",
      content: '""',
      display: "block",
      height: "2px",
      width: 0,
      transform: "translateX(-50%)",
      transition: "2s cubic-bezier(0.22, 1, 0.36, 1)",
      position: "absolute",
      bottom: 0,
      left: "50%",
    },
    "&.is-animated::after": { width: "100%" },
  },
}));

const PAGE_SIZE = 20;

const PostSearch = () => {
  const api = useAPI();
  const { t } = useTranslation();
  const classes = useStyles();

  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState([]);
  const [animate, setAnimate] = useState(false);

  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef(null);

  useEffect(() => setAnimate(true), []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = inputValue.trim();
    setItems([]);
    setCount(0);
    setHasMore(true);
    setSearchTerm(q);
  };

  const load = useCallback(
    async (c) => {
      if (loading || !hasMore) return;
      setLoading(true);
      try {
        const res = await api.searchPosts({ q: searchTerm || "", count: c || 0 });
        const list = Array.isArray(res?.res) ? res.res : [];
        const total = typeof res?.totalCount === "number" ? res.totalCount : undefined;

        setItems((prev) => (c === 0 ? list : [...prev, ...list]));

        if (total !== undefined) {
          const loaded = (c + 1) * PAGE_SIZE;
          setHasMore(list.length > 0 && loaded < total + PAGE_SIZE);
        } else {
          setHasMore(list.length > 0);
        }
      } catch {
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [api, searchTerm, loading, hasMore]
  );

  // 初回/語変更時に1ページ目
  useEffect(() => {
    setItems([]);
    setCount(0);
    setHasMore(true);
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // 無限スクロール
  useEffect(() => {
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && hasMore) {
          const next = count + 1;
          setCount(next);
          load(next);
        }
      },
      { root: null, rootMargin: "0px", threshold: 1.0 }
    );
    if (loaderRef.current) io.observe(loaderRef.current);
    return () => io.disconnect();
  }, [count, hasMore, loading, load]);

  return (
    <Fragment>
      <Header />
      <Leftnav />

      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">
            <div className="row">
              <div className="col-xl-12">
                {/* 見出し + 検索フォーム */}
                <div className="card shadow-xss w-100 d-block d-flex border-0 p-4 mb-3">
                  <h2 className="fw-700 mb-0 mt-0 font-md text-grey-900 d-flex align-items-center">
                    {t("PostSearch1")}
                    <form className="pt-0 pb-0 ms-auto" onSubmit={handleSubmit}>
                      <div className="search-form-2 ms-1">
                        <i className="ti-search font-xss" />
                        <input
                          type="text"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          className="form-control text-grey-500 mb-0 bg-greylight theme-dark-bg border-0"
                          placeholder={t("PostSearch2")}
                        />
                      </div>
                    </form>
                  </h2>
                </div>

                {/* ガイドライン（アニメ線） */}
                {/* <div
                  className={`${classes.line} ${animate ? "is-animated" : ""} font-xssss text-center text-current fw-600 mt-0 mb-2`}
                >
                  {inputValue ? (t("SearchResults") || "検索結果") : (t("LatestPosts") || "最新の投稿")}
                </div> */}

                {/* 検索結果（Postview を縦積み） */}
                <div className="row ps-3 pe-3">
                  <div className="col-12">
                    {items.length > 0 ? (
                      items.map((p, idx) => (
                        <Postview key={p.postId || p.id || idx} post={p} />
                      ))
                    ) : !loading ? (
                      <div className="font-xssss text-grey-600 text-center mt-3">{t("PostSearch3")}</div>
                    ) : null}
                  </div>
                </div>

                {/* センチネル */}
                <div ref={loaderRef} style={{ height: 20, margin: "0 auto" }} />

                {/* ローディング */}
                {loading && (
                  <div className="card-body p-0 mb-3">
                    <div className="card w-100 shadow-xss rounded-xxl border-0 p-4 mb-3">
                      <div className="snippet mt-2 ms-auto me-auto" data-title=".dot-typing">
                        <div className="stage">
                          <div className="dot-typing" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-4 mb-3" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Appfooter />
    </Fragment>
  );
};

export default PostSearch;
